import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import studyService from '../../services/studyService';
import { Book, Hash, Globe, AlertCircle, ChevronLeft, ChevronDown, Briefcase, Check } from 'lucide-react';

// Required field marker component for consistency
const RequiredFieldMarker = () => (
  <span className="text-red-500 ml-1 dark:text-red-400" aria-label="required field">*</span>
);

// Character counter component
const CharacterCounter = ({ current, max }) => (
  <div className="mt-1 text-right text-xs text-gray-500 dark:text-gray-400">
    {current.length} / {max} characters
  </div>
);

function CreateStudy() {
  const navigate = useNavigate();
  const [studyData, setStudyData] = useState({
    code: '',
    title: '',
    description: '',
    status: 'planned',
    is_byod_allowed: false,
    min_participant_id: '',
    max_participant_id: '',
  });

  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');

  // Constants for validation
  const LIMITS = {
    code: 20,
    title: 100,
    description: 500,
    participant_id: 10
  };

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    
    if (type === 'checkbox') {
      setStudyData(prev => ({ ...prev, [name]: checked }));
      return;
    }

    // Validation for participant IDs (numbers only, max 10 chars)
    if (name === 'min_participant_id' || name === 'max_participant_id') {
      if (value === '' || (/^\d+$/.test(value) && value.length <= LIMITS.participant_id)) {
        setStudyData(prev => ({ ...prev, [name]: value }));
      }
      return;
    }

    // Character limits for other fields
    const limits = {
      code: LIMITS.code,
      title: LIMITS.title,
      description: LIMITS.description
    };

    if (limits[name] && value.length <= limits[name]) {
      setStudyData(prev => ({ ...prev, [name]: value }));
    } else if (!limits[name]) {
      setStudyData(prev => ({ ...prev, [name]: value }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!studyData.code.trim()) {
      newErrors.code = 'Study code is required';
    }

    if (!studyData.title.trim()) {
      newErrors.title = 'Study title is required';
    }

    if (studyData.min_participant_id && !studyData.max_participant_id) {
      newErrors.max_participant_id = 'Max participant ID is required when min ID is set';
    }

    if (!studyData.min_participant_id && studyData.max_participant_id) {
      newErrors.min_participant_id = 'Min participant ID is required when max ID is set';
    }

    if (studyData.min_participant_id && studyData.max_participant_id) {
      const min = parseInt(studyData.min_participant_id);
      const max = parseInt(studyData.max_participant_id);
      if (min >= max) {
        newErrors.min_participant_id = 'Min ID must be less than Max ID';
        newErrors.max_participant_id = 'Max ID must be greater than Min ID';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!validateForm()) {
      toast.error('Please correct the errors before submitting.');
      return;
    }

    try {
      const result = await studyService.createStudy(studyData);
      toast.success('Study created successfully!');
      setTimeout(() => navigate('/studies'), 500);
    } catch (err) {
      console.error('Error creating study:', err);
      const errorMessage = err.response?.data?.error || 'Failed to create study. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const fieldIconClass = 'pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500';

  return (
    <div className="max-w-3xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        className="mb-1"
      >
        <Link
          to="/studies"
          className="back-link"
        >
          <ChevronLeft className="h-4 w-4" />
          <span>Back to Studies</span>
        </Link>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        className="card overflow-hidden"
      >
        <div className="card-band">
          <h2 className="card-band-title">Create New Study</h2>
        </div>
        <div className="p-6">
          <div className="mb-5 text-sm text-gray-500 dark:text-gray-400">
            Fields marked with <RequiredFieldMarker /> are required
          </div>

          {error && (
            <div className="mb-4 rounded-lg border border-danger-100 bg-danger-50 p-4 text-sm text-danger-700 dark:border-danger-500/30 dark:bg-danger-500/10 dark:text-danger-400">
              <div className="flex items-center">
                <AlertCircle className="mr-2 h-4 w-4 flex-shrink-0" />
                <p>{error}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="title" className="label">
                Study Title<RequiredFieldMarker />
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={studyData.title}
                onChange={handleInputChange}
                className={`input ${errors.title ? 'input-error' : ''}`}
                placeholder="Enter the title of the study"
                required
              />
              <CharacterCounter current={studyData.title} max={LIMITS.title} />
              {errors.title && <p className="field-error">{errors.title}</p>}
            </div>

            <div>
              <label htmlFor="description" className="label">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                value={studyData.description}
                onChange={handleInputChange}
                rows="3"
                className="input"
                placeholder="Provide a brief description of the study"
              />
              <CharacterCounter current={studyData.description} max={LIMITS.description} />
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <label htmlFor="code" className="label">
                  Study Code<RequiredFieldMarker />
                </label>
                <div className="relative">
                  <Briefcase className={fieldIconClass} size={16} />
                  <input
                    type="text"
                    id="code"
                    name="code"
                    value={studyData.code}
                    onChange={handleInputChange}
                    className={`input pl-10 ${errors.code ? 'input-error' : ''}`}
                    placeholder="e.g., STU001"
                    required
                  />
                </div>
                <CharacterCounter current={studyData.code} max={LIMITS.code} />
                {errors.code && <p className="field-error">{errors.code}</p>}
              </div>

              <div>
                <label htmlFor="status" className="label">
                  Status
                </label>
                <div className="relative">
                  <Globe className={fieldIconClass} size={16} />
                  <select
                    id="status"
                    name="status"
                    value={studyData.status}
                    onChange={handleInputChange}
                    className="input appearance-none pl-10 pr-9"
                  >
                    <option value="planned">Planned</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" size={16} />
                </div>
              </div>

              <div>
                <label htmlFor="min_participant_id" className="label">
                  Min Participant ID
                </label>
                <div className="relative">
                  <Hash className={fieldIconClass} size={16} />
                  <input
                    type="text"
                    id="min_participant_id"
                    name="min_participant_id"
                    value={studyData.min_participant_id}
                    onChange={handleInputChange}
                    className={`input pl-10 ${errors.min_participant_id ? 'input-error' : ''}`}
                    placeholder="e.g., 1001"
                  />
                </div>
                {errors.min_participant_id && (
                  <p className="field-error">{errors.min_participant_id}</p>
                )}
              </div>

              <div>
                <label htmlFor="max_participant_id" className="label">
                  Max Participant ID
                </label>
                <div className="relative">
                  <Hash className={fieldIconClass} size={16} />
                  <input
                    type="text"
                    id="max_participant_id"
                    name="max_participant_id"
                    value={studyData.max_participant_id}
                    onChange={handleInputChange}
                    className={`input pl-10 ${errors.max_participant_id ? 'input-error' : ''}`}
                    placeholder="e.g., 2000"
                  />
                </div>
                {errors.max_participant_id && (
                  <p className="field-error">{errors.max_participant_id}</p>
                )}
              </div>
            </div>

            <div className="flex items-center">
              <label className="flex cursor-pointer items-center">
                <div className="relative">
                  <input
                    type="checkbox"
                    id="is_byod_allowed"
                    name="is_byod_allowed"
                    checked={studyData.is_byod_allowed}
                    onChange={handleInputChange}
                    className="peer sr-only"
                  />
                  <div className={`flex h-5 w-5 items-center justify-center rounded-md border-2 transition-colors duration-150 peer-focus-visible:ring-2 peer-focus-visible:ring-primary-500/60 peer-focus-visible:ring-offset-2 dark:peer-focus-visible:ring-offset-gray-800 ${studyData.is_byod_allowed ? 'border-primary-600 bg-primary-600 dark:border-primary-500 dark:bg-primary-500' : 'border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-800'}`}>
                    <Check className={`h-3.5 w-3.5 text-white transition-opacity duration-150 ${studyData.is_byod_allowed ? 'opacity-100' : 'opacity-0'}`} strokeWidth={3} />
                  </div>
                </div>
                <span className="ml-2.5 text-sm font-medium text-gray-700 dark:text-gray-200">
                  Allow BYOD (Bring Your Own Device)
                </span>
              </label>
            </div>

            <div className="flex justify-end gap-3 border-t border-gray-100 pt-6 dark:border-gray-700">
              <button
                type="button"
                onClick={() => navigate('/studies')}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
              >
                Create Study
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </div>
  );
}

export default CreateStudy;